/**
 * ShipperSection — "(d) Shipper & receiver details" on the one-page Confirm &
 * book. Ports the ESSENTIAL fields of the legacy ShipperBasicForm (company,
 * contact, address) — the party on the other end of the shipment. The heading
 * follows the customer's role: an Importer tells us about their SHIPPER, an
 * Exporter about their RECEIVER (same rule as the legacy tab).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Select } from 'antd';
import countryList from 'react-select-country-list';
import {
  SectionCard,
  SectionHeading,
  SectionHint,
  FieldGrid,
  FullRow,
  fieldLabelStyle,
} from './confirmTheme';

const { Option } = Select;

const label = (text) => <span style={fieldLabelStyle}>{text}</span>;

const ShipperSection = ({ form, role }) => {
  const { getFieldDecorator } = form;
  const options = countryList().getData();
  const isImporter = role !== 'Exporter';
  const partyNoun = isImporter ? 'shipper' : 'receiver';

  return (
    <SectionCard aria-label="Shipper and receiver details">
      <SectionHeading>{isImporter ? 'Your shipper' : 'Your receiver'}</SectionHeading>
      <SectionHint>
        {isImporter
          ? 'Who is shipping this cargo? Prime Freight or our team in the region of departure will coordinate with them.'
          : 'Who receives this cargo? Prime Freight will coordinate the delivery with them.'}
      </SectionHint>

      <FieldGrid>
        <FullRow>
          <Form.Item label={label(`${partyNoun} company name`)} colon={false}>
            {getFieldDecorator('shipper_company', {
              validateTrigger: 'onBlur',
              rules: [
                {
                  required: true,
                  message: `Please add your ${partyNoun}’s company name.`,
                },
              ],
            })(<Input size="large" placeholder="Company name" />)}
          </Form.Item>
        </FullRow>
        <Form.Item label={label('Contact first name (optional)')} colon={false}>
          {getFieldDecorator('shipper_first_name')(
            <Input size="large" placeholder="First name" />
          )}
        </Form.Item>
        <Form.Item label={label('Contact last name (optional)')} colon={false}>
          {getFieldDecorator('shipper_last_name')(
            <Input size="large" placeholder="Last name" />
          )}
        </Form.Item>
        <FullRow>
          <Form.Item label={label('Email (optional)')} colon={false}>
            {getFieldDecorator('shipper_email', {
              validateTrigger: 'onBlur',
              rules: [
                {
                  type: 'email',
                  message: 'That email doesn’t look right — please check it.',
                },
              ],
            })(<Input size="large" type="email" placeholder="contact@company.com" />)}
          </Form.Item>
        </FullRow>
        <FullRow>
          <Form.Item label={label('Street address')} colon={false}>
            {getFieldDecorator('shipper_street', {
              validateTrigger: 'onBlur',
              rules: [
                {
                  required: true,
                  message: `Please add your ${partyNoun}’s street address.`,
                },
              ],
            })(<Input size="large" placeholder="Street address" />)}
          </Form.Item>
        </FullRow>
        <FullRow>
          <Form.Item label={label('Address line 2 (optional)')} colon={false}>
            {getFieldDecorator('shipper_address2')(
              <Input size="large" placeholder="Suite, unit, floor…" />
            )}
          </Form.Item>
        </FullRow>
        <Form.Item label={label('City')} colon={false}>
          {getFieldDecorator('shipper_city', {
            validateTrigger: 'onBlur',
            rules: [
              { required: true, message: `Please add your ${partyNoun}’s city.` },
            ],
          })(<Input size="large" placeholder="City" />)}
        </Form.Item>
        <Form.Item label={label('State / Province (optional)')} colon={false}>
          {getFieldDecorator('shipper_state')(
            <Input size="large" placeholder="State / Province" />
          )}
        </Form.Item>
        <Form.Item label={label('ZIP / Postal code (optional)')} colon={false}>
          {getFieldDecorator('shipper_postal')(
            <Input size="large" placeholder="ZIP / Postal code" />
          )}
        </Form.Item>
        <Form.Item label={label('Country')} colon={false}>
          {getFieldDecorator('shipper_country', {
            rules: [
              { required: true, message: `Please pick your ${partyNoun}’s country.` },
            ],
          })(
            <Select
              size="large"
              showSearch
              placeholder="Select country"
              optionFilterProp="children"
            >
              {options.map((country) => (
                <Option key={country.value} value={country.label}>
                  {country.label}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </FieldGrid>
    </SectionCard>
  );
};

ShipperSection.propTypes = {
  form: PropTypes.object.isRequired,
  /** 'Importer' | 'Exporter' — the live role field value */
  role: PropTypes.string,
};

export default ShipperSection;
